/**
 * Componente CustomCursor
 * Cursor personalizado premium estilo Awwwards
 * 
 * @description
 * Cursor minimalista con color sólido uniforme
 * - Punto oscuro que sigue instantáneamente
 * - Círculo con borde que sigue con inercia y escala en hover
 * - Color uniforme sin cambios de blend mode
 * 
 * @principios
 * - Single Responsibility: Solo visualización del cursor
 * - Simplicidad: Usa requestAnimationFrame para fluidez
 */

import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const CustomCursor = () => {
  const dotRef = useRef(null);
  const circleRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const dotPos = useRef({ x: -100, y: -100 });
  const circlePos = useRef({ x: -100, y: -100 });
  const rafId = useRef(null);
  const isHovering = useRef(false);

  useEffect(() => {
    // No ejecutar en dispositivos táctiles
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    const dot = dotRef.current;
    const circle = circleRef.current;
    if (!dot || !circle) return;

    // Ocultar cursor nativo
    const styleEl = document.createElement('style');
    styleEl.id = 'hide-native-cursor';
    styleEl.textContent = '*, *::before, *::after { cursor: none !important; }';
    document.head.appendChild(styleEl);

    // Loop de animación con lerp para suavidad
    const animate = () => {
      // Punto sigue rápido (lerp 0.35)
      dotPos.current.x += (pos.current.x - dotPos.current.x) * 0.35;
      dotPos.current.y += (pos.current.y - dotPos.current.y) * 0.35;
      dot.style.transform = `translate(${dotPos.current.x}px, ${dotPos.current.y}px)`;

      // Círculo sigue más lento (lerp 0.15) para efecto de inercia
      circlePos.current.x += (pos.current.x - circlePos.current.x) * 0.15;
      circlePos.current.y += (pos.current.y - circlePos.current.y) * 0.15;
      circle.style.transform = `translate(${circlePos.current.x}px, ${circlePos.current.y}px) scale(${isHovering.current ? 1.5 : 1})`;

      rafId.current = requestAnimationFrame(animate);
    };

    // Seguimiento del mouse
    const onMouseMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      
      // Mostrar cursor si estaba oculto
      if (dot.style.opacity === '0') {
        gsap.to([dot, circle], { opacity: 1, duration: 0.3 });
      }
    };

    // Detectar elementos interactivos
    const onMouseOver = (e) => {
      let el = e.target;
      const interactiveSelectors = 'a, button, [role="button"], input, textarea, select, [data-cursor], .cursor-pointer, [onclick]';
      
      while (el && el !== document.body) {
        if (el.matches?.(interactiveSelectors)) {
          isHovering.current = true;
          gsap.to(dot, { 
            scale: 0,
            duration: 0.2 
          });
          return;
        }
        el = el.parentElement;
      }
    };

    const onMouseOut = (e) => {
      // No resetear si entramos en otro elemento interactivo
      const related = e.relatedTarget;
      if (related) {
        let el = related;
        const selectors = 'a, button, [role="button"], input, textarea, select, [data-cursor], .cursor-pointer';
        while (el && el !== document.body) {
          if (el.matches?.(selectors)) return;
          el = el.parentElement;
        }
      }

      isHovering.current = false;
      gsap.to(dot, { 
        scale: 1,
        duration: 0.3,
        ease: 'elastic.out(1, 0.5)'
      });
    };

    // Click feedback
    const onMouseDown = () => {
      gsap.to(circle, { scale: 0.8, duration: 0.1 });
      gsap.to(dot, { scale: 0.5, duration: 0.1 });
    };

    const onMouseUp = () => {
      gsap.to(circle, { 
        scale: isHovering.current ? 1.5 : 1, 
        duration: 0.4, 
        ease: 'elastic.out(1, 0.4)' 
      });
      gsap.to(dot, { 
        scale: isHovering.current ? 0 : 1, 
        duration: 0.4, 
        ease: 'elastic.out(1, 0.4)' 
      });
    };

    // Ocultar cuando sale de la ventana
    const onMouseLeave = () => {
      gsap.to([dot, circle], { opacity: 0, duration: 0.3 });
    };

    const onMouseEnter = () => {
      gsap.to([dot, circle], { opacity: 1, duration: 0.3 });
    };

    // Event listeners
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.addEventListener('mouseover', onMouseOver, { passive: true });
    document.addEventListener('mouseout', onMouseOut, { passive: true });
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onMouseUp);
    document.documentElement.addEventListener('mouseleave', onMouseLeave);
    document.documentElement.addEventListener('mouseenter', onMouseEnter);

    // Iniciar animación
    rafId.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseover', onMouseOver);
      document.removeEventListener('mouseout', onMouseOut);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
      document.documentElement.removeEventListener('mouseenter', onMouseEnter);
      const style = document.getElementById('hide-native-cursor');
      if (style) style.remove();
    };
  }, []);

  // No renderizar en dispositivos táctiles
  if (typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
    return null;
  }

  return (
    <>
      {/* Círculo exterior - Amarillo Intercasa para máxima visibilidad en todos los fondos */}
      <div
        ref={circleRef}
        className="cursor-personalizado"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 40,
          height: 40,
          marginLeft: -20,
          marginTop: -20,
          borderRadius: '50%',
          border: '2px solid #F5D72F',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          zIndex: 99998,
          opacity: 0,
          transition: 'border-color 0.3s ease',
          willChange: 'transform',
        }}
      />

      {/* Punto central - Amarillo Intercasa */}
      <div
        ref={dotRef}
        className="cursor-personalizado"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 8,
          height: 8,
          marginLeft: -4,
          marginTop: -4,
          borderRadius: '50%',
          backgroundColor: '#F5D72F',
          pointerEvents: 'none',
          zIndex: 99999,
          opacity: 0,
          willChange: 'transform',
        }}
      />
    </>
  );
};

export default CustomCursor;
